/**
 * Admin — Onglet Paiements (Phase 6).
 * Lecture seule. FR uniquement (admin reste FR+Light).
 */
import { useEffect, useState } from 'react';
import { adminPayments } from '@/lib/api';
import { CreditCard, RefreshCw } from 'lucide-react';

const STATUS_LABELS = {
  initiated: { label: 'En cours', color: '#0EA5E9' },
  complete: { label: 'Payé', color: '#10B981' },
  expired: { label: 'Expiré', color: '#8892A6' },
  failed: { label: 'Échec', color: '#F43F5E' },
};

const PLAN_LABELS = {
  basique: 'Basique',
  pro: 'Pro',
};

function formatDate(iso) {
  try {
    return new Date(iso).toLocaleString('fr-FR', {
      day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

function formatAmount(cents, currency = 'eur') {
  const value = (cents || 0) / 100;
  try {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency', currency: currency.toUpperCase(), maximumFractionDigits: 0,
    }).format(value);
  } catch {
    return `${value.toFixed(0)} ${currency.toUpperCase()}`;
  }
}

export default function PaymentsTab() {
  const [data, setData] = useState({ items: [], total: 0, by_status: {} });
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const limit = 20;

  const load = async () => {
    setLoading(true);
    try {
      const res = await adminPayments({ page, limit });
      setData(res);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  const totalPages = Math.max(1, Math.ceil((data.total || 0) / limit));

  return (
    <div className="space-y-6" data-testid="admin-payments-panel">
      {/* Overview */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <div className="rounded-2xl border border-[var(--om-border)] bg-white p-4" data-testid="admin-payments-total">
          <div className="text-xs font-semibold uppercase tracking-wider text-[var(--om-muted-2)]">Transactions</div>
          <div className="mt-1 text-2xl font-extrabold tabular-nums text-[var(--om-ink)]">{data.total}</div>
        </div>
        {['initiated', 'complete', 'expired', 'failed'].map((s) => (
          <div key={s} className="rounded-2xl border border-[var(--om-border)] bg-white p-4" data-testid={`admin-payments-stat-${s}`}>
            <div className="text-xs font-semibold uppercase tracking-wider text-[var(--om-muted-2)]">
              {STATUS_LABELS[s]?.label || s}
            </div>
            <div className="mt-1 text-2xl font-extrabold tabular-nums text-[var(--om-ink)]" style={{ color: STATUS_LABELS[s]?.color }}>
              {data.by_status?.[s] || 0}
            </div>
          </div>
        ))}
      </div>

      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-[var(--om-muted-2)]">
          <CreditCard size={12} strokeWidth={2.4} /> Historique
        </div>
        <button
          type="button"
          onClick={load}
          className="inline-flex items-center gap-1.5 rounded-full border border-[var(--om-border)] bg-white px-3 py-1.5 text-xs font-semibold text-[var(--om-ink)] hover:bg-[var(--om-bg)]"
          data-testid="admin-payments-refresh"
        >
          <RefreshCw size={12} strokeWidth={2.4} className={loading ? 'animate-spin' : ''} /> Rafraîchir
        </button>
      </div>

      <div className="overflow-hidden rounded-2xl border border-[var(--om-border)] bg-white">
        <table className="min-w-full divide-y divide-[var(--om-border)]" data-testid="admin-payments-table">
          <thead className="bg-[var(--om-bg)]">
            <tr>
              <th className="px-4 py-3 text-left text-[10px] font-bold uppercase tracking-wider text-[var(--om-muted-2)]">Date</th>
              <th className="px-4 py-3 text-left text-[10px] font-bold uppercase tracking-wider text-[var(--om-muted-2)]">Plan</th>
              <th className="px-4 py-3 text-left text-[10px] font-bold uppercase tracking-wider text-[var(--om-muted-2)]">Cycle</th>
              <th className="px-4 py-3 text-right text-[10px] font-bold uppercase tracking-wider text-[var(--om-muted-2)]">Montant</th>
              <th className="px-4 py-3 text-left text-[10px] font-bold uppercase tracking-wider text-[var(--om-muted-2)]">Email</th>
              <th className="px-4 py-3 text-left text-[10px] font-bold uppercase tracking-wider text-[var(--om-muted-2)]">Statut</th>
              <th className="px-4 py-3 text-left text-[10px] font-bold uppercase tracking-wider text-[var(--om-muted-2)]">Session</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-[var(--om-border)]">
            {(data.items || []).map((it) => {
              const st = STATUS_LABELS[it.status] || { label: it.status, color: '#0A0E1A' };
              return (
                <tr key={it.session_id} data-testid={`admin-payment-row-${it.session_id}`}>
                  <td className="whitespace-nowrap px-4 py-3 text-xs text-[var(--om-muted)]">{formatDate(it.created_at)}</td>
                  <td className="whitespace-nowrap px-4 py-3 text-sm font-semibold text-[var(--om-ink)]">{PLAN_LABELS[it.plan] || it.plan}</td>
                  <td className="whitespace-nowrap px-4 py-3 text-xs text-[var(--om-muted)]">{it.interval === 'year' ? 'Annuel' : 'Mensuel'}</td>
                  <td className="whitespace-nowrap px-4 py-3 text-right text-sm font-semibold tabular-nums text-[var(--om-ink)]">{formatAmount(it.amount, it.currency)}</td>
                  <td className="whitespace-nowrap px-4 py-3 text-xs text-[var(--om-ink-soft)]">{it.email || <span className="text-[var(--om-muted-2)]">—</span>}</td>
                  <td className="whitespace-nowrap px-4 py-3">
                    <span
                      className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider"
                      style={{ color: st.color, background: `${st.color}18` }}
                    >
                      {st.label}
                    </span>
                  </td>
                  <td className="whitespace-nowrap px-4 py-3 text-[10px] font-mono text-[var(--om-muted-2)]">{it.session_id.slice(0, 14)}…</td>
                </tr>
              );
            })}
            {(!data.items || data.items.length === 0) && (
              <tr>
                <td colSpan={7} className="px-4 py-10 text-center text-sm text-[var(--om-muted-2)]" data-testid="admin-payments-empty">
                  Aucune transaction pour l'instant.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="flex items-center justify-between text-xs text-[var(--om-muted-2)]">
        <div>Page {page} / {totalPages}</div>
        <div className="flex gap-2">
          <button
            type="button"
            disabled={page <= 1}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            className="rounded-full border border-[var(--om-border)] bg-white px-3 py-1.5 font-semibold text-[var(--om-ink)] disabled:opacity-50"
          >
            Précédent
          </button>
          <button
            type="button"
            disabled={page >= totalPages}
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            className="rounded-full border border-[var(--om-border)] bg-white px-3 py-1.5 font-semibold text-[var(--om-ink)] disabled:opacity-50"
          >
            Suivant
          </button>
        </div>
      </div>
    </div>
  );
}
